import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Chip,
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  School as SubjectIcon,
} from '@mui/icons-material';
import { useCreateIncident } from '../../hooks/useBehaviorCases';
import type { CreateIncidentInput } from '../../services/behaviorService';

// ═══════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════

const INCIDENT_TYPES = [
  { value: 'disruption', label: 'Disruption', color: '#ff9800' },
  { value: 'aggression', label: 'Aggression', color: '#f44336' },
  { value: 'off_task', label: 'Off-Task', color: '#2196f3' },
  { value: 'defiance', label: 'Defiance', color: '#9c27b0' },
  { value: 'emotional_dysregulation', label: 'Emotional', color: '#795548' },
  { value: 'other', label: 'Other', color: '#757575' },
] as const;

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: '#4caf50' },
  { value: 'moderate', label: 'Moderate', color: '#ff9800' },
  { value: 'high', label: 'High', color: '#f44336' },
  { value: 'crisis', label: 'Crisis', color: '#b71c1c' },
] as const;

const LOCATIONS = [
  { value: 'classroom', label: 'Classroom' },
  { value: 'hallway', label: 'Hallway' },
  { value: 'cafeteria', label: 'Cafeteria' },
  { value: 'playground', label: 'Playground' },
  { value: 'library', label: 'Library' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'other', label: 'Other' },
] as const;

const TIME_PERIODS = [
  { value: 'morning', label: 'Morning' },
  { value: 'mid_morning', label: 'Mid-Morning' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'end_of_day', label: 'End of Day' },
] as const;

const SUBJECTS = [
  'Math',
  'Reading',
  'Writing',
  'Science',
  'Social Studies',
  'Art',
  'PE',
  'Music',
  'Transition',
  'Recess',
  'Lunch',
  'Other',
];

// ═══════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════

interface IncidentLoggerProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  studentName?: string;
}

const IncidentLogger: React.FC<IncidentLoggerProps> = ({
  open,
  onClose,
  caseId,
  studentName,
}) => {
  const createIncident = useCreateIncident();
  
  // Form state
  const [incidentType, setIncidentType] = useState<CreateIncidentInput['incidentType'] | null>(null);
  const [severity, setSeverity] = useState<CreateIncidentInput['severity'] | null>(null);
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState<CreateIncidentInput['location']>(undefined);
  const [timeOfDay, setTimeOfDay] = useState<CreateIncidentInput['timeOfDay']>(undefined);
  const [subjectArea, setSubjectArea] = useState('');
  const [antecedent, setAntecedent] = useState('');
  const [consequence, setConsequence] = useState('');
  const [staffResponse, setStaffResponse] = useState('');
  const [duration, setDuration] = useState<number | ''>('');
  const [incidentTime, setIncidentTime] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm format
  });

  const [error, setError] = useState<string | null>(null);
  const [showOptional, setShowOptional] = useState(false);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setIncidentType(null);
      setSeverity(null);
      setDescription('');
      setLocation(undefined);
      setTimeOfDay(undefined);
      setSubjectArea('');
      setAntecedent('');
      setConsequence('');
      setStaffResponse('');
      setDuration('');
      setIncidentTime(new Date().toISOString().slice(0, 16));
      setError(null);
      setShowOptional(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    // Validation
    if (!incidentType) {
      setError('Please select an incident type');
      return;
    }
    if (!severity) {
      setError('Please select a severity level');
      return;
    }
    if (!description.trim()) {
      setError('Please provide a description');
      return;
    }

    setError(null);

    try {
      await createIncident.mutateAsync({
        caseId,
        data: {
          incidentType,
          severity,
          description: description.trim(),
          location: location || undefined,
          timeOfDay: timeOfDay || undefined,
          subjectArea: subjectArea || undefined,
          antecedent: antecedent.trim() || undefined,
          consequence: consequence.trim() || undefined,
          staffResponse: staffResponse.trim() || undefined,
          duration: duration ? Number(duration) : undefined,
          incidentAt: new Date(incidentTime).toISOString(),
        },
      });

      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save incident');
    }
  }, [
    caseId,
    incidentType,
    severity,
    description,
    location,
    timeOfDay,
    subjectArea,
    antecedent,
    consequence,
    staffResponse,
    duration,
    incidentTime,
    createIncident,
    onClose,
  ]);

  const isSubmitting = createIncident.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Log Behavior Incident</Typography>
          {studentName && (
            <Typography variant="caption" color="text.secondary">
              Student: {studentName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Incident Type */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Incident Type *
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {INCIDENT_TYPES.map((type) => (
                <Chip
                  key={type.value}
                  label={type.label}
                  onClick={() => setIncidentType(type.value)}
                  sx={{
                    backgroundColor: incidentType === type.value ? type.color : 'default',
                    color: incidentType === type.value ? '#fff' : 'inherit',
                    '&:hover': {
                      backgroundColor: incidentType === type.value ? type.color : 'action.hover',
                    },
                  }}
                />
              ))}
            </Box>
          </Grid>

          {/* Severity */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Severity *
            </Typography>
            <ToggleButtonGroup
              value={severity}
              exclusive
              onChange={(_, value) => value && setSeverity(value)}
              sx={{ width: '100%' }}
            >
              {SEVERITY_LEVELS.map((level) => (
                <ToggleButton
                  key={level.value}
                  value={level.value}
                  sx={{
                    flex: 1,
                    backgroundColor: severity === level.value ? `${level.color} !important` : undefined,
                    color: severity === level.value ? '#fff !important' : undefined,
                    borderColor: level.color,
                  }}
                >
                  {level.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          {/* Time */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Time of Incident"
              type="datetime-local"
              value={incidentTime}
              onChange={(e) => setIncidentTime(e.target.value)}
              InputProps={{
                startAdornment: <TimeIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Grid>

          {/* Duration */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={duration}
              onChange={(e) => setDuration(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="Optional"
              inputProps={{ min: 0, max: 480 }}
            />
          </Grid>

          {/* Location & Context */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={location}
                label="Location"
                onChange={(e) => setLocation(e.target.value as CreateIncidentInput['location'])}
              >
                <MenuItem value="">None</MenuItem>
                {LOCATIONS.map((loc) => (
                  <MenuItem key={loc.value} value={loc.value}>
                    {loc.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Time of Day</InputLabel>
              <Select
                value={timeOfDay}
                label="Time of Day"
                onChange={(e) => setTimeOfDay(e.target.value as CreateIncidentInput['timeOfDay'])}
              >
                <MenuItem value="">None</MenuItem>
                {TIME_PERIODS.map((time) => (
                  <MenuItem key={time.value} value={time.value}>
                    {time.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subjectArea}
                label="Subject"
                onChange={(e) => setSubjectArea(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {SUBJECTS.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description *"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what happened..."
              required
            />
          </Grid>

          {/* Optional Fields Toggle */}
          <Grid item xs={12}>
            <Button
              variant="text"
              onClick={() => setShowOptional(!showOptional)}
              sx={{ textTransform: 'none' }}
            >
              {showOptional ? 'Hide' : 'Show'} Optional Fields (ABC Data)
            </Button>
          </Grid>

          {/* ABC Fields (Optional) */}
          {showOptional && (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Antecedent"
                  multiline
                  rows={2}
                  value={antecedent}
                  onChange={(e) => setAntecedent(e.target.value)}
                  placeholder="What happened before the incident?"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Consequence"
                  multiline
                  rows={2}
                  value={consequence}
                  onChange={(e) => setConsequence(e.target.value)}
                  placeholder="What happened after the incident?"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Staff Response"
                  multiline
                  rows={2}
                  value={staffResponse}
                  onChange={(e) => setStaffResponse(e.target.value)}
                  placeholder="How did staff respond?"
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !incidentType || !severity || !description.trim()}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Saving...' : 'Log Incident'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentLogger;
